import { FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material'
import { BookingsResponseDto } from '@planning/app/api'
import { CellWithTitleAndSubtitle } from '@planning/components/atoms/CellWithTitleAndSubtitle'
import { emptyIndicator } from '@planning/constants'
import { IPaginatedStoreWithItems } from '@planning/stores/PaginatedStore'
import { formatDateTime } from '@planning/utils'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  OptionsButton,
  TomTable,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { PATHS, useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useNavigate } from 'react-router'

interface Props {
  store: IPaginatedStoreWithItems<BookingsResponseDto>
  showCompleted: boolean
  onShowCompletedChange: (value: boolean) => void
}

export const BookingsTable = observer(({ store, showCompleted, onShowCompletedChange }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()

  const columns = useMemo<MRT_ColumnDef<BookingsResponseDto>[]>(
    () => [
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        accessorKey: 'referenceNumber',
        header: t('bookingNo', 'Booking no.'),
        Cell: cell => {
          const { row } = cell
          return (
            <CellWithTitleAndSubtitle
              title={row.original.referenceNumber ?? emptyIndicator}
              subtitle={row.original.shippingLine ?? emptyIndicator}
            />
          )
        },
      },
      {
        ...tomTableColumnFilterPresets.searchMandatory(),
        header: t('carrier', 'Carrier'),
        accessorKey: 'identifier',
        accessorFn: row => row.carrierVisit?.identifier,
        Cell: cell => {
          const { row } = cell
          const carrierVisit = row.original.carrierVisit

          return (
            <CellWithTitleAndSubtitle
              title={carrierVisit?.identifier ?? emptyIndicator}
              subtitle={`ETA: ${formatDateTime(carrierVisit?.eta) ?? emptyIndicator}`}
            />
          )
        },
      },
      {
        ...tomTableColumnFilterPresets.searchOptional(),
        header: t('tripIds', 'Trip IDs'),
        accessorKey: 'tripIds',
        accessorFn: row => {
          const inboundTripIds =
            row.inboundTripIds && row.inboundTripIds.length > 0 && row.inboundTripIds[0] !== ''
          const outboundTripIds =
            row.outboundTripIds && row.outboundTripIds.length > 0 && row.outboundTripIds[0] !== ''
          if (inboundTripIds || outboundTripIds) {
            return `${inboundTripIds ? row.inboundTripIds?.join(', ') : ''}${outboundTripIds ? row.outboundTripIds?.join(', ') : ''}`
          } else {
            return null
          }
        },
        Cell: cell => {
          const { row } = cell
          const booking = row.original

          const inboundTripIds =
            booking.inboundTripIds &&
            booking.inboundTripIds.length > 0 &&
            booking.inboundTripIds[0] !== ''

          const outboundTripIds =
            booking.outboundTripIds &&
            booking.outboundTripIds.length > 0 &&
            booking.outboundTripIds[0] !== ''

          return (
            <Stack>
              {inboundTripIds && (
                <Stack flexDirection='row' gap={1}>
                  <Typography variant='body2'>{t('in', 'In')}:</Typography>
                  <Typography variant='body2'>
                    {booking.inboundTripIds?.filter((id: string) => id.trim() !== '').join(', ')}
                  </Typography>
                </Stack>
              )}
              {outboundTripIds && (
                <Stack flexDirection='row' gap={1}>
                  <Typography variant='body2'>{t('out', 'Out')}:</Typography>
                  <Typography variant='body2'>
                    {booking.outboundTripIds?.filter((id: string) => id.trim() !== '').join(', ')}
                  </Typography>
                </Stack>
              )}
              {!inboundTripIds && !outboundTripIds && emptyIndicator}
            </Stack>
          )
        },
      },
      {
        ...tomTableColumnFilterPresets.searchOptional(),
        accessorKey: 'portOfDischarge',
        header: t('pod', 'POD'),
      },
      {
        ...tomTableColumnFilterPresets.searchOptional(),
        accessorKey: 'finalDestination',
        header: t('finalDestination', 'Final Destination'),
      },
      {
        ...tomTableColumnFilterPresets.numericMandatory(),
        accessorKey: 'units',
        header: t('units', 'Units'),
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('planning/bookings')

  const table = useTomTable(
    {
      columns: columns,
      data: store.pageItems,
      layoutMode: 'grid',
      enableRowSelection: false,
      enableExpanding: false,
      renderTopToolbarCustomActions: () => (
        <FormControlLabel
          control={
            <Switch
              checked={showCompleted}
              onChange={event => onShowCompletedChange(event.target.checked)}
              color='primary'
              data-cy='toggle-completed-switch'
            />
          }
          label={t('showCompletedBookings', 'Show completed bookings')}
        />
      ),
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 230px)',
          minHeight: 'calc(100vh - 230px)',
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () =>
              navigate(
                `${PATHS.orders.bookings.url}/${row.original.referenceNumber}/visit/${row.original.carrierVisit?.id}/orders`,
              ),
            testId: 'view-bookings-option',
          },
        ]
        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='bookings-options-btn'
          />
        )
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <Paper variant='elevation' elevation={1} sx={{ overflow: 'hidden' }}>
      <TomTable table={table} />
    </Paper>
  )
})
